import { type CustomTooltipProps } from "ag-grid-react"

import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import { StatusBadge } from "~/components/design-system/components"
import SuperChart, { type CategoryChartSeries } from "~/components/reusable/graphs/SuperChart"
import { ChartType } from "~/constants/graphs"
import { type WeeksOnHandExplanation } from "~/utils/weeksOnHand"

import HeaderTooltip from "./HeaderTooltip"
import TooltipMessageCard from "./TooltipMessageCard"

const WeeksOnHandTooltip = (props: CustomTooltipProps) => {
  if (props.location === "header") return <HeaderTooltip {...props} />
  if (!props.node?.group) return null

  // The fashion buy table sends a bare value list; ProductTableV2 sends values plus an explanation.
  const explanation: WeeksOnHandExplanation | undefined = props.value?.explanation
  const weeksOnHandValues: number[] = Array.isArray(props.value)
    ? props.value
    : (props.value?.values ?? [])

  const STEPS = [0, 5, 10, 15, 20, 30, 50]
  const buckets = new Map<string, number>()

  let naCount = 0
  for (const rawWeeks of weeksOnHandValues) {
    if (!isFinite(rawWeeks)) {
      naCount++
      continue
    }
    const weeks = Math.max(0, rawWeeks)
    if (weeks >= 50) {
      buckets.set("50+", (buckets.get("50+") ?? 0) + 1)
    } else {
      for (let i = STEPS.length - 1; i >= 0; i--) {
        if (weeks >= STEPS[i]!) {
          const label = `${STEPS[i]}–${STEPS[i + 1]}`
          buckets.set(label, (buckets.get(label) ?? 0) + 1)
          break
        }
      }
    }
  }

  const orderedBuckets: [string, number][] = []
  for (let i = 0; i < STEPS.length - 1; i++) {
    const label = `${STEPS[i]}–${STEPS[i + 1]}`
    const count = buckets.get(label)
    if (count) orderedBuckets.push([label, count])
  }
  const overflowCount = buckets.get("50+")
  if (overflowCount) orderedBuckets.push(["50+", overflowCount])
  if (naCount > 0) orderedBuckets.push(["N/A", naCount])

  // One populated bucket is not a distribution: every leaf sits in the range the cell already
  // shows. `buckets` holds only numeric ranges, so N/A cannot inflate the count.
  if (buckets.size <= 1)
    return explanation ? (
      <TooltipMessageCard badge={explanation.badge}>{explanation.message}</TooltipMessageCard>
    ) : null

  const chartSeries: CategoryChartSeries = {
    name: "Variants",
    type: "column",
    colors: [],
    data: orderedBuckets.map(([label, count]) => ({
      category: label,
      value: count,
    })),
  }

  return (
    <div className="bg-base w-100 mr-6 z-10">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Weeks on hand distribution</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {explanation && (
            <div className="flex flex-col items-start gap-1.5">
              <StatusBadge text={explanation.badge.text} color={explanation.badge.color} />
              <span className="paragraph-sm text-fg-subtle">{explanation.message}</span>
            </div>
          )}
          <SuperChart
            type={ChartType.CategorySeries}
            data={[chartSeries]}
            height={150}
            compact
            showTooltip={false}
            showDataLabels
            showYAxisLabels
            showXAxisLabels
            showTickLines={false}
            disableAnimation
            xAxisTitle="Weeks on hand"
            yAxisTitle="Variants"
          />
        </CardContent>
      </Card>
    </div>
  )
}

export default WeeksOnHandTooltip
